/**
 * Capital held against an option position, computed from its OPEN legs — never from the chain's
 * strategy label, which has been wrong on rolled chains. Spec: docs/superpowers/specs/
 * 2026-09-10-capital-held-design.md. Pure: no dates, no I/O.
 */
export type HeldKind = 'put_spread' | 'call_spread' | 'cash_secured_put' | 'uncovered_call' | 'debit';
export type HeldFlag = 'uncovered_call' | 'unpriced_leg';
export interface HeldLegInput {
  optionType: 'put' | 'call';
  action: 'BTO' | 'STO' | 'BTC' | 'STC';
  strikePrice: number;
  expiration: string;          // YYYY-MM-DD
  quantityRemaining: number;
  pricePerContract: number;
}
export interface HeldSide {
  optionType: 'put' | 'call'; expiration: string; held: number; kind: HeldKind; contracts: number;
  shortStrike: number | null; longStrike: number | null;
}
export interface CapitalHeld { held: number | null; kinds: HeldKind[]; flags: HeldFlag[]; sides: HeldSide[]; freesUpOn: string | null }

const isShort = (a: HeldLegInput['action']) => a === 'STO' || a === 'STC';
/**
 * A leg nobody could price: any of strike, quantity or price missing or not a number. All three
 * matter — a non-finite quantity would otherwise be dropped silently by the consolidator and a
 * non-finite price would reach `held` as NaN on the debit path, both under-reporting with no flag.
 */
const unpriced = (l: HeldLegInput) =>
  !Number.isFinite(l.strikePrice) || !Number.isFinite(l.quantityRemaining) || !Number.isFinite(l.pricePerContract);
/** Finite and nothing left open: not a data problem, just closed. Dropped without a flag. */
const notOpen = (l: HeldLegInput) => Number.isFinite(l.quantityRemaining) && l.quantityRemaining <= 0;

/** One entry per (type, strike, expiration, side); partial fills summed; nothing-remaining dropped. */
export function consolidateHeldLegs(legs: HeldLegInput[]): HeldLegInput[] {
  const out = new Map<string, HeldLegInput>();
  for (const l of legs) {
    if (!(l.quantityRemaining > 0)) continue;
    const key = `${l.optionType}|${l.strikePrice}|${l.expiration}|${isShort(l.action) ? 'S' : 'L'}`;
    const cur = out.get(key);
    if (cur) {
      // weighted average price so a debit computed from fills is right
      const q = cur.quantityRemaining + l.quantityRemaining;
      cur.pricePerContract = (cur.pricePerContract * cur.quantityRemaining + l.pricePerContract * l.quantityRemaining) / q;
      cur.quantityRemaining = q;
    } else out.set(key, { ...l, action: isShort(l.action) ? 'STO' : 'BTO' });
  }
  return [...out.values()];
}

interface Group { optionType: 'put' | 'call'; expiration: string; shorts: HeldLegInput[]; longs: HeldLegInput[] }

function sideOf(g: Group): { sides: HeldSide[]; flags: HeldFlag[] } {
  const sides: HeldSide[] = []; const flags: HeldFlag[] = [];
  // Pair shorts with the nearest protective long: below for puts, above for calls.
  const longs = g.longs.map((l) => ({ ...l })).sort((a, b) => g.optionType === 'put' ? b.strikePrice - a.strikePrice : a.strikePrice - b.strikePrice);
  // Premium taken in by shorts that are the short leg of a DEBIT spread, netted off the debit below.
  const debitClaimed = new Map<HeldLegInput, number>();
  let debitOffset = 0;
  for (const s of [...g.shorts].sort((a, b) => a.strikePrice - b.strikePrice)) {
    let remaining = s.quantityRemaining;
    for (const l of longs) {
      const protects = g.optionType === 'put' ? l.strikePrice < s.strikePrice : l.strikePrice > s.strikePrice;
      if (!protects || l.quantityRemaining <= 0 || remaining <= 0) continue;
      const n = Math.min(remaining, l.quantityRemaining);
      const width = Math.abs(s.strikePrice - l.strikePrice);
      sides.push({ optionType: g.optionType, expiration: g.expiration, held: width * 100 * n, kind: g.optionType === 'put' ? 'put_spread' : 'call_spread', contracts: n, shortStrike: s.strikePrice, longStrike: l.strikePrice });
      l.quantityRemaining -= n; remaining -= n;
    }
    // A short on the FAR side of a long held against it is not naked: a long call below a short
    // call (or a long put above a short put) is a debit spread, whose requirement is the net
    // debit paid, accounted for on the leftover-long side below. The long keeps its quantity —
    // it IS the money held — so coverage is tracked separately from `quantityRemaining`.
    for (const l of longs) {
      if (remaining <= 0) break;
      const covers = g.optionType === 'put' ? l.strikePrice > s.strikePrice : l.strikePrice < s.strikePrice;
      const free = l.quantityRemaining - (debitClaimed.get(l) ?? 0);
      if (!covers || free <= 0) continue;
      const n = Math.min(remaining, free);
      debitClaimed.set(l, (debitClaimed.get(l) ?? 0) + n);
      debitOffset += s.pricePerContract * 100 * n;
      remaining -= n;
    }
    if (remaining > 0) {
      if (g.optionType === 'put') sides.push({ optionType: 'put', expiration: g.expiration, held: s.strikePrice * 100 * remaining, kind: 'cash_secured_put', contracts: remaining, shortStrike: s.strikePrice, longStrike: null });
      else { flags.push('uncovered_call'); sides.push({ optionType: 'call', expiration: g.expiration, held: 0, kind: 'uncovered_call', contracts: remaining, shortStrike: s.strikePrice, longStrike: null }); }
    }
  }
  // Longs left over (debit side): what was paid for them, net of the premium taken in by the
  // shorts those longs cover (a debit spread's short is covered by the long, so the position's
  // requirement is the NET debit). Only the quantity actually claimed above is netted.
  const leftoverLongs = longs.filter((l) => l.quantityRemaining > 0);
  if (leftoverLongs.length > 0) {
    const paid = leftoverLongs.reduce((sum, l) => sum + l.pricePerContract * 100 * l.quantityRemaining, 0) - debitOffset;
    const n = leftoverLongs.reduce((sum, l) => sum + l.quantityRemaining, 0);
    sides.push({ optionType: g.optionType, expiration: g.expiration, held: Math.max(0, paid), kind: 'debit', contracts: n, shortStrike: null, longStrike: leftoverLongs[0].strikePrice });
  }
  return { sides, flags };
}

/**
 * Capital held by a set of sides, grouped by expiration.
 *
 * Condor rule: a put spread and a call spread at the SAME expiration hold the wider side only —
 * only one of them can finish in the money, so the broker holds one requirement, not two. Spreads
 * at different expirations are independent and add. Everything else at that expiration (a
 * cash-secured put, a debit) adds on top; an uncovered call has no quantifiable requirement and
 * contributes zero (it is reported through `flags`/`kinds`, not through the number).
 *
 * Exported because two callers need it: `capitalHeld` for a chain's own total, and the API's
 * capital report for the per-expiration subtotal across chains. Written once so they cannot drift.
 */
export function heldForSides(sides: HeldSide[]): number {
  const byExpiration = new Map<string, HeldSide[]>();
  for (const s of sides) byExpiration.set(s.expiration, [...(byExpiration.get(s.expiration) ?? []), s]);
  let held = 0;
  for (const group of byExpiration.values()) {
    const put = group.filter((s) => s.kind === 'put_spread').reduce((a, s) => a + s.held, 0);
    const call = group.filter((s) => s.kind === 'call_spread').reduce((a, s) => a + s.held, 0);
    const rest = group
      .filter((s) => s.kind !== 'put_spread' && s.kind !== 'call_spread' && s.kind !== 'uncovered_call')
      .reduce((a, s) => a + s.held, 0);
    held += (put > 0 && call > 0 ? Math.max(put, call) : put + call) + rest;
  }
  return held;
}

export function capitalHeld(rawLegs: HeldLegInput[]): CapitalHeld {
  const flags: HeldFlag[] = [];
  const usable: HeldLegInput[] = [];
  let unpricedCount = 0;
  for (const l of rawLegs) {
    if (notOpen(l)) continue;
    if (unpriced(l)) { unpricedCount += 1; continue; }
    usable.push(l);
  }
  if (unpricedCount > 0) flags.push('unpriced_leg');
  const legs = consolidateHeldLegs(usable);
  const groups = new Map<string, Group>();
  for (const l of legs) {
    const key = `${l.optionType}|${l.expiration}`;
    const g = groups.get(key) ?? { optionType: l.optionType, expiration: l.expiration, shorts: [], longs: [] };
    (isShort(l.action) ? g.shorts : g.longs).push(l);
    groups.set(key, g);
  }
  const sides: HeldSide[] = [];
  for (const g of groups.values()) { const r = sideOf(g); sides.push(...r.sides); flags.push(...r.flags); }

  const held = heldForSides(sides);
  // Whether ANY side is quantifiable at all. An all-uncovered chain holds an unknown amount, not
  // zero, so it reports null rather than the 0 `heldForSides` returns for it.
  const any = sides.some((s) => s.kind !== 'uncovered_call');
  const unique = <T,>(xs: T[]) => [...new Set(xs)];
  const heldSides = sides.filter((s) => s.kind !== 'uncovered_call');
  // Nothing priceable is not "nothing held": a live position nobody could price reports null.
  // Only genuinely empty (or entirely closed) input holds zero.
  const nothingPriceable = sides.length === 0 && unpricedCount > 0;
  return {
    held: sides.length === 0 ? (nothingPriceable ? null : 0) : any ? held : null,
    kinds: unique(sides.map((s) => s.kind)),
    flags: unique(flags),
    sides,
    freesUpOn: heldSides.length ? heldSides.map((s) => s.expiration).sort().at(-1)! : null,
  };
}
